import _ from "lodash"
import { useRef, useEffect, useState, useMemo, useCallback } from "react"
import Graph from "node-dijkstra"
import { captureException } from "@sentry/react"
import {
  prepareRouteSteps,
  findLocatedUnit,
  createDescription,
  createArrivalStep,
  findNearestFeature,
  findNearbyPointOfInterest,
  groupFeatureByOrdinal,
} from "providers/venue/utils/routeStep"
import {
  calculateTotalDistance,
  calculateRoundedDistance,
  calculateTravelingDuration,
  findPathOnArea,
  transformMultiPolygonToPolygons,
} from "../utils/routePath"
import { getCenterFromGeometry } from "utils/geometry"
import difference from "@turf/difference"
import envelope from "@turf/envelope"
import booleanOverlap from "@turf/boolean-overlap"
import booleanIntersects from "@turf/boolean-intersects"
import { featureCollection, isNumber, point, polygon } from "@turf/helpers"
import { IFeature, IRoute, IStep, IStepElement, ISequence } from "interfaces"
import { calculateDisplacement } from "../utils/routeStep"

const OBSTACLE_FEATURE_TYPES = ["kiosk", "fixture"]
const OBSTACLE_CATEGORIES = [
  "nonpublic",
  "opentobelow",
  "elevator",
  "escalator",
  "stairs",
  "room",
  "unspecified",
]
// Walkable category should be further update in the future
const WALKABLE_CATEGORY = [
  "walkway",
  "parking",
  "room",
  "unenclosedarea",
  "unspecified",
]
const VERTICAL_TRAVERSAL_CATEGORY = ["elevator", "escalator", "stairs", "ramp"]

const featureIsVerticalTraversalUnit = (feature) => {
  return VERTICAL_TRAVERSAL_CATEGORY.indexOf(feature.properties.category) > -1
}

const featureIsOccupantWithUnit = (feature) => {
  const { feature_type, properties } = feature
  return feature_type === "occupant" && properties.unit_id
}

const featureIsUnit = (feature) => {
  const { feature_type } = feature
  return feature_type === "unit"
}

/**
 *
 * routeOrigin: "kiosk1"
 * routeDest: "occupant2"
 * dijkstra ["ww1", "ww2", "es1", "es2", "ww3"]
 * sequence ["ww1 (origin: kiosk1)", "ww2", ["es1", "es2"], "ww3", "unit2 (dest: occupant2)",]
 */

const createSequencesFromDijkstraPath = (
  dijkstraPathUnits,
  routeOrigin,
  routeDestination
): ISequence[] => {
  /* Note: Could be adjust how to create elevator in Dijkstra Graph in the future */
  const originIsOccupantWithUnit = featureIsOccupantWithUnit(routeOrigin)
  const originIsUnit = featureIsUnit(routeOrigin)
  const destinationIsOccupantWithUnit =
    featureIsOccupantWithUnit(routeDestination)
  const destinationIsUnit = featureIsUnit(routeDestination)
  const sequences = dijkstraPathUnits
    /**
     * Filter any unit that prev unit + next unit are both escalators | elevators | stairs
     */
    .reduce((result, unit, index, units) => {
      const lastUnit = units[index - 1]
      const isFirstUnit = index === 0
      const isLastUnit = index === units.length - 1
      // In case of master origin/destination is occupant and the destination is unit
      if (
        ((destinationIsOccupantWithUnit || destinationIsUnit) && isLastUnit) ||
        ((originIsOccupantWithUnit || originIsUnit) && isFirstUnit)
      ) {
        return result
      }
      if (
        lastUnit &&
        featureIsVerticalTraversalUnit(unit) &&
        lastUnit.properties.category === unit.properties.category
      ) {
        const lastSequence = result[result.length - 1]
        lastSequence.intermediary.push(unit)
        lastSequence.goingTo = units[index + 1] || routeDestination
        return result
      }

      return [
        ...result,
        {
          comingFrom: index === 0 ? routeOrigin : units[index - 1],
          goingTo: units[index + 1] || routeDestination,
          intermediary: [unit],
          category: unit.properties.category,
        },
      ]
    }, [])

  return sequences
}

export const useShortestPath = (
  { relationships, features, units, kiosks, fixtures, occupants },
  { enabled = true }
) => {
  const relationshipGraphRef = useRef(null)
  const elevatorPreferredRelationshipGraphRef = useRef(null)
  const [relationshipGraphLoaded, setRelationshipGraphLoaded] = useState(false)
  const featureObj = features.reduce(
    (acc, feature) => ({ ...acc, [feature.id]: feature }),
    {}
  )
  const possibleObstacleFeatures = useMemo(
    () => [...units, ...kiosks, ...fixtures],
    [units, kiosks, fixtures]
  )

  const landmarks = useMemo(
    () => features.filter((feature) => feature.properties.is_landmark),
    [features]
  )

  useEffect(() => {
    // initiate graph
    if (!enabled) return
    if (relationships.length > 0) {
      const filteredNonStepRelationships = relationships.filter(
        (relationship) =>
          !["stairs", "escalator"].includes(relationship.properties.category)
      )
      relationshipGraphRef.current = new Graph(prepareRouteSteps(relationships))

      elevatorPreferredRelationshipGraphRef.current = new Graph(
        prepareRouteSteps(filteredNonStepRelationships)
      )
    }
    setRelationshipGraphLoaded(true)
  }, [relationships, enabled])

  const groupedOccupantsByOrdinal = useMemo(
    () =>
      _(occupants)
        .filter(
          (occupant) =>
            !_.isNil(
              occupant.properties.anchor?.properties.unit?.properties.ordinal
            )
        )
        .groupBy(
          (occupant) =>
            occupant.properties.anchor?.properties.unit?.properties.ordinal
        )
        .value(),
    [occupants]
  )

  const groupedLandmarksByOrdinal = useMemo(
    () => groupFeatureByOrdinal(landmarks),
    [landmarks]
  )

  // grouping the other obstacle category by ordinal (except the categoris in the relationship)
  const groupedOtherObstacleByOrdinal = useMemo(
    () =>
      _(possibleObstacleFeatures)
        .filter(
          ({ feature_type, properties }) =>
            (isNumber(properties.ordinal) &&
              OBSTACLE_FEATURE_TYPES.includes(feature_type)) ||
            OBSTACLE_CATEGORIES.includes(properties.category)
        )

        .map(({ geometry, properties = {}, id, feature_type }) => {
          const obstacleProperties = { id, feature_type, ...properties }
          return geometry.type === "Polygon"
            ? polygon(geometry.coordinates, obstacleProperties)
            : transformMultiPolygonToPolygons(geometry, obstacleProperties)
        })
        .flatten()
        .groupBy((feature) => feature.properties.ordinal)
        .value(),
    [possibleObstacleFeatures]
  )

  const findObstaclesByOrdinal = useCallback(
    (ordinal) => groupedOtherObstacleByOrdinal[ordinal],
    [groupedOtherObstacleByOrdinal]
  )

  const filterRelationships = useCallback(
    (from: IFeature, to: IFeature): IFeature[] => {
      return relationships.filter(
        ({ properties: { origin, destination } }) =>
          (destination?.id === to.id && origin.id === from.id) ||
          (destination?.id === from.id && origin.id === to.id)
      )
    },
    [relationships]
  )

  const findObstaclesFromWalkway = useCallback(
    (walkway, exceptionIds = []) => {
      const result = featureCollection([])

      if (!walkway) return result

      const walkwayOrdinal = walkway.properties.ordinal
      const obstacleOnOrdinal = findObstaclesByOrdinal(walkwayOrdinal).filter(
        (obstacle) => !exceptionIds.includes(obstacle.properties.id)
      )

      const relatedObstacleWithWalkway = obstacleOnOrdinal.reduce(
        (obstacles, feature) => {
          if (
            // Prevent detecting itself as an obstacle
            // Ex. Unable to draw a line to amenity located with in a room as room is also consider as obstacle
            feature.properties.id !== walkway.id &&
            (booleanOverlap(walkway, feature) ||
              booleanIntersects(walkway, feature))
          )
            obstacles.push(polygon(feature.geometry.coordinates))
          return obstacles
        },
        []
      )

      const walkwayExtends = envelope(walkway)
      const walkwayPerimeter = difference(walkwayExtends, walkway)

      result.features.push(...relatedObstacleWithWalkway, walkwayPerimeter)
      return result
    },
    [findObstaclesByOrdinal]
  )

  const findStepPath = useCallback(
    (originStep, destinationStep, intermediaries, comingFrom, goingTo) => {
      // find the walkway relates the step's opening
      const relatedWalkablePlatform = intermediaries.find((feature) =>
        WALKABLE_CATEGORY.includes(feature.properties.category)
      )
      // TODO: Temporary solution as an urgent fix may be this should be fix later
      const exceptionFeatureIds = []
      if (goingTo.feature_type === "occupant") {
        const occupantKiosk = goingTo.properties.kiosk
        if (!!occupantKiosk) exceptionFeatureIds.push(occupantKiosk?.id)
      }
      if (comingFrom.feature_type === "occupant") {
        const occupantKiosk = comingFrom.properties.kiosk
        if (!!occupantKiosk) exceptionFeatureIds.push(occupantKiosk?.id)
      }
      // find obstacles from the unit relates the walkway and the kiosk on target level
      const obstacles = findObstaclesFromWalkway(
        relatedWalkablePlatform,
        _.compact(exceptionFeatureIds)
      )

      // set origin and destination point
      const originPoint = getCenterFromGeometry(originStep?.geometry)
      const destinationPoint = getCenterFromGeometry(destinationStep?.geometry)

      // find path and return LineString
      return findPathOnArea(originPoint, destinationPoint, {
        obstacles,
      })
    },
    [findObstaclesFromWalkway]
  )

  const findUnitConnection = useCallback(
    (unit, target, origin = null) => {
      let stepOrigin
      if (target.feature_type === "occupant") {
        stepOrigin = target.properties.anchor
      } else if (
        featureIsUnit(target) &&
        target.properties.category !== "unenclosedarea"
      ) {
        const originRelationships = filterRelationships(target, unit)
        const nearestRelationship = _.minBy(
          originRelationships,
          (relationship) => {
            const intermediary: IFeature | undefined = _.first(
              relationship?.properties.intermediary
            )
            const { geometry } = intermediary || {}

            const destination = origin || target
            const distance = calculateDisplacement(
              destination?.geometry,
              geometry
            )
            return distance
          }
        ) // { intermediary: Opening }
        stepOrigin = _.first(nearestRelationship?.properties.intermediary) // First Opening
      } else if (target.feature_type === "kiosk") {
        stepOrigin = target.properties.anchor
      } else {
        stepOrigin = target
      }
      return stepOrigin
    },
    [filterRelationships]
  )

  const createRouteForSameWalkway = useCallback(
    (
      routeOrigin: IFeature,
      routeDestination: IFeature,
      walkwayUnit: IFeature
    ): IRoute => {
      const stepOrigin = findUnitConnection(walkwayUnit, routeOrigin)
      const stepDestination = findUnitConnection(walkwayUnit, routeDestination)
      const steps: IStep[] = [
        {
          origin: stepOrigin,
          comingFrom: routeOrigin,
          goingTo: routeDestination,
          intermediary: [walkwayUnit],
          description: createDescription(
            routeOrigin,
            routeDestination,
            [walkwayUnit],
            true
          ),
          destination: stepDestination,
          path: findStepPath(
            stepOrigin,
            stepDestination,
            [walkwayUnit],
            routeOrigin,
            routeDestination
          ),
        },
      ]
      steps.push(createArrivalStep(steps) as IStep)
      const totalDistance = calculateTotalDistance(steps) // distance in meters
      const roundedDistance = calculateRoundedDistance(totalDistance)
      const duration = calculateTravelingDuration(totalDistance)
      return {
        origin: routeOrigin,
        destination: routeDestination,
        description: null,
        duration: duration,
        distance: roundedDistance,
        steps,
      }
    },
    [findStepPath, findUnitConnection]
  )

  const createSteps = useCallback(
    (sequences: ISequence[]): IStep[] => {
      const steps = sequences.reduce((acc, sequence, index) => {
        const { comingFrom, goingTo, intermediary } = sequence
        const isLastStep = index === sequences.length - 1
        const prevSequence = acc[acc.length - 1]
        const stepOrigin = prevSequence
          ? prevSequence.destination
          : findUnitConnection(_.first(intermediary), comingFrom)
        const stepDestination = findUnitConnection(
          _.last(intermediary),
          goingTo,
          stepOrigin
        )

        const occupantInSameOrdinal =
          groupedOccupantsByOrdinal[goingTo.properties.ordinal]

        const landmarkInSameOrdinal =
          groupedLandmarksByOrdinal[goingTo.properties.ordinal]

        const nearestLandmark = findNearestFeature(
          landmarkInSameOrdinal,
          stepDestination
        )

        const nearestShop = findNearestFeature(
          occupantInSameOrdinal,
          stepDestination
        )

        const nearestPointOfInterest =
          // Added fallback in case there is nothing available
          findNearbyPointOfInterest(
            nearestLandmark,
            nearestShop,
            stepDestination
          ) || goingTo

        const isGoingToVerticalTraversal = VERTICAL_TRAVERSAL_CATEGORY.includes(
          goingTo.properties.category
        )
        return [
          ...acc,
          {
            origin: stepOrigin,
            destination: stepDestination,
            comingFrom,
            goingTo,
            intermediary,
            category: _.first(intermediary).properties.category,
            description: createDescription(
              comingFrom,
              isGoingToVerticalTraversal ? goingTo : nearestPointOfInterest,
              intermediary,
              nearestPointOfInterest,
              isLastStep
            ),
            path: findStepPath(
              stepOrigin,
              stepDestination,
              intermediary,
              goingTo,
              comingFrom
            ),
          },
        ]
      }, [])
      steps.push(createArrivalStep(steps))
      return steps
    },
    [
      findUnitConnection,
      findStepPath,
      groupedOccupantsByOrdinal,
      groupedLandmarksByOrdinal,
    ]
  )

  const caculateRoute = useCallback(
    (
      routeOrigin: IFeature,
      routeDestination: IFeature,
      relatioshipGraph
    ): IRoute => {
      // If relationshipGraph is not loaded, return null
      if (!relationshipGraphLoaded || !routeOrigin || !routeDestination)
        return null

      try {
        const routeOriginUnit: IFeature = findLocatedUnit(routeOrigin, units)
        const routeDestinationUnit: IFeature = findLocatedUnit(
          routeDestination,
          units
        )
        // Incase that origin and destination is located on the same unit i.e. atm -> atm on the same walkway
        if (routeOriginUnit.id === routeDestinationUnit.id) {
          return createRouteForSameWalkway(
            routeOrigin,
            routeDestination,
            routeOriginUnit
          )
        }

        // Return Array of unit id along the way
        const dijkstraPath = relatioshipGraph.path(
          routeOriginUnit.id,
          routeDestinationUnit.id
        )

        const dijkstraPathUnits = dijkstraPath.map(
          (unitId) => featureObj[unitId]
        )

        const sequences: ISequence[] = createSequencesFromDijkstraPath(
          dijkstraPathUnits,
          routeOrigin,
          routeDestination
        )

        const steps: IStep[] = createSteps(sequences)

        const totalDistance = calculateTotalDistance(steps) // distance in meters
        const roundedDistance = calculateRoundedDistance(totalDistance)
        const duration = calculateTravelingDuration(totalDistance)
        return {
          origin: routeOrigin,
          destination: routeDestination,
          description: null,
          distance: roundedDistance,
          duration,
          steps,
        }
      } catch (error) {
        captureException(error, (scope) => {
          scope.setTransactionName(`Error: Unable to find route.`)
          return scope
        })
        throw error
      }
    },
    [
      createSteps,
      featureObj,
      relationshipGraphLoaded,
      units,
      createRouteForSameWalkway,
    ]
  )
  const findRoute = useCallback(
    (routeOrigin, routeDestination) => {
      return caculateRoute(
        routeOrigin,
        routeDestination,
        relationshipGraphRef.current
      )
    },
    [caculateRoute]
  )

  const findElevatorPreferredRoute = (routeOrigin, routeDestination) => {
    return caculateRoute(
      routeOrigin,
      routeDestination,
      elevatorPreferredRelationshipGraphRef.current
    )
  }

  const getOrdinalFromStep = useCallback(({ properties }) => {
    const { ordinal, unit } = properties
    return _.isNumber(ordinal) ? ordinal : unit.properties.ordinal
  }, [])

  const transformStepsToGeometries = useCallback(
    (steps = []) => {
      const firstStep = steps[0]
      const lastStep = steps[steps.length - 1]
      const routeOrigin: IStepElement = {
        id: "master-origin-marker",
        element_type: "origin-marker",
        geometry: point(getCenterFromGeometry(firstStep.origin.geometry))
          ?.geometry,
        properties: {
          ordinal: getOrdinalFromStep(firstStep.origin),
        },
      }
      const routeDestination: IStepElement = {
        id: "master-destination-marker",
        element_type: "destination-marker",
        geometry: point(getCenterFromGeometry(lastStep.origin.geometry))
          ?.geometry,
        properties: {
          ordinal: getOrdinalFromStep(lastStep.destination),
        },
      }

      const stepGeometries = steps.map((step, index) => {
        const { origin, intermediary } = step
        // check the step is the vertical step or not
        const isVerticalStep =
          intermediary.length > 0 &&
          intermediary.every((feature) =>
            VERTICAL_TRAVERSAL_CATEGORY.includes(feature.properties.category)
          )
        // get unit ids which are intermediaries
        const intermediaryIds = intermediary.map((unit) => unit.id)

        let stepPath = step.path
        // transform Line String apth to point if the step is the vertical step
        if (isVerticalStep) stepPath = point(step.path.geometry.coordinates[0])

        return {
          ...stepPath,
          isVerticalStep: isVerticalStep,
          isLastStep: steps.length - 1 === index,
          properties: {
            ordinal: getOrdinalFromStep(origin),
            intermediaryUnits: intermediaryIds,
          },
          id: `step-${index + 1}`,
        }
      })
      stepGeometries.unshift(routeOrigin)
      stepGeometries.push(routeDestination)
      return stepGeometries
    },
    [getOrdinalFromStep]
  )

  return {
    relationshipGraphLoaded,
    findRoute: _.memoize(
      findRoute,
      (origin, destination) => `${origin.id} ${destination.id}`
    ),
    findElevatorPreferredRoute: _.memoize(
      findElevatorPreferredRoute,
      (origin, destination) => `${origin.id} ${destination.id}`
    ),
    transformStepsToGeometries,
  }
}
